import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';

import { ActionMap } from '@root/types';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

export const searchLocations = [
  'Chats',
  'Projects',
  'Tasks',
  'Files',
  'Contacts',
] as const;
export type SearchLocation = (typeof searchLocations)[number];

enum SearchActionType {
  setSearch,
  setViewAllEvent,
  setIsSearchFocus,
  setLocation,
  setIsSearching,
  storeRecentSearchTerm,
  setHistory,
  deleteHistoryItem,
  setSearchWidth,
}

type SearchStatePayload = {
  [SearchActionType.setSearch]: string;
  [SearchActionType.setIsSearchFocus]: boolean;
  [SearchActionType.setViewAllEvent]: boolean;
  [SearchActionType.setLocation]: SearchLocation;
  [SearchActionType.setIsSearching]: boolean;
  [SearchActionType.storeRecentSearchTerm]: string;
  [SearchActionType.setHistory]: HistoryItem[];
  [SearchActionType.deleteHistoryItem]: string;
  [SearchActionType.setSearchWidth]: number;
};

type HistoryItem = { id: string; value: string };

type GlobalSearchState = {
  search: string;
  isSearchFocus: boolean;
  isViewAllPress: boolean;
  location: SearchLocation[];
  isSearching: boolean;
  history: HistoryItem[];
  searchWidth: number;
};

type SearchContext = GlobalSearchState & {
  setSearch: (search: GlobalSearchState['search']) => void;
  setIsSearchFocus: (focus: GlobalSearchState['isSearchFocus']) => void;
  setViewAllEvent: (focus: GlobalSearchState['isViewAllPress']) => void;
  setLocation: (filter: GlobalSearchState['location'][number]) => void;
  setIsSearching: (isSearching: GlobalSearchState['isSearching']) => void;
  storeRecentSearchTerm: () => void;
  deleteHistoryItem: (historyItemId: string) => void;
  setSearchWidth: (collapsed: GlobalSearchState['searchWidth']) => void;
};

const initialSearchState: GlobalSearchState = {
  search: '',
  isSearchFocus: false,
  isViewAllPress: true,
  location: [],
  isSearching: false,
  history: [],
  searchWidth: 500,
};

type SearchActions =
  ActionMap<SearchStatePayload>[keyof ActionMap<SearchStatePayload>];
const reducer = (
  state: GlobalSearchState,
  action: SearchActions
): GlobalSearchState => {
  switch (action.type) {
    case SearchActionType.setSearch:
      return {
        ...state,
        isSearching: true,
        search: action.payload,
      };
    case SearchActionType.setIsSearchFocus:
      return {
        ...state,
        isSearching: action.payload,
        isSearchFocus: action.payload,
      };
    case SearchActionType.setViewAllEvent:
      return {
        ...state,
        isViewAllPress: action.payload,
      };
    case SearchActionType.setLocation: {
      const filterIndex = state.location.indexOf(action.payload);
      return {
        ...state,
        isSearching: true,
        location:
          filterIndex === -1
            ? [action.payload]
            : state.location.filter((_, i) => i !== filterIndex),
      };
    }
    case SearchActionType.setIsSearching: {
      if (action.payload) {
        return { ...state, isSearching: true };
      } else {
        return {
          ...state,
          isSearching: false,
          isViewAllPress: false,
          search: '',
          location: [],
        };
      }
    }
    case SearchActionType.setHistory: {
      return {
        ...state,
        history: action.payload,
      };
    }
    case SearchActionType.deleteHistoryItem: {
      const newHistory = state.history.filter((i) => i.id !== action.payload);
      const jsonValue = JSON.stringify(newHistory);

      AsyncStorage.setItem('@search_history', jsonValue);
      return {
        ...state,
        history: newHistory,
      };
    }
    case SearchActionType.setSearchWidth:
      return {
        ...state,
        searchWidth: action.payload,
      };
    default:
      return state;
  }
};

const searchContext = createContext<SearchContext | undefined>(undefined);

export const GlobalSearchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { Provider } = searchContext;
  const { trackEvent } = useMixpanel();
  const lastTrackedQueryRef = useRef('');
  const [state, dispatch] = useReducer(reducer, {
    ...initialSearchState,
  });

  const setSearch = (search: typeof state.search) => {
    dispatch({ type: SearchActionType.setSearch, payload: search });
  };
  const setViewAllEvent = (search: typeof state.isViewAllPress) => {
    dispatch({ type: SearchActionType.setViewAllEvent, payload: search });
  };

  const setIsSearchFocus = (focus: typeof state.isSearchFocus) => {
    dispatch({ type: SearchActionType.setIsSearchFocus, payload: focus });
  };

  const setLocation = (filter: (typeof state.location)[number]) => {
    dispatch({ type: SearchActionType.setLocation, payload: filter });
  };

  const setIsSearching = (isSearching: typeof state.isSearching) => {
    if (!isSearching) lastTrackedQueryRef.current = '';
    dispatch({ type: SearchActionType.setIsSearching, payload: isSearching });
  };

  const setHistory = (history: typeof state.history) => {
    dispatch({ type: SearchActionType.setHistory, payload: history });
  };

  const setSearchWidth = (focus: typeof state.searchWidth) => {
    dispatch({ type: SearchActionType.setSearchWidth, payload: focus });
  };

  const deleteHistoryItem = (historyItemId: string) => {
    dispatch({
      type: SearchActionType.deleteHistoryItem,
      payload: historyItemId,
    });
  };

  const storeRecentSearchTerm = async () => {
    // whatever is in search, store it
    if (state.search.trim().length > 0) {
      const query = state.search.trim();
      // Deduplicate: only fire once per unique query to avoid double-tracking
      // when storeRecentSearchTerm is called from multiple code paths.
      if (query !== lastTrackedQueryRef.current) {
        lastTrackedQueryRef.current = query;
        trackEvent(MIXPANEL_EVENTS.SEARCH_PERFORMED, {
          query,
          // Always an array so the property type is consistent in Mixpanel;
          // empty array means the user searched across all locations.
          location: state.location,
        });
      }
      // remove oldest history item, add new history item
      try {
        const newHistory = [];
        const filteredHistory = state.history.filter(
          (i) => i.value.trim() !== state.search.trim()
        );

        // if you did filter, slice - if not, remain.
        if (filteredHistory.length === state.history.length) {
          newHistory.push(...state.history.slice(0, 4));
        } else {
          newHistory.push(...filteredHistory);
        }

        newHistory.push({
          id: Date.now().toString(),
          value: state.search,
        });

        const jsonValue = JSON.stringify(newHistory);

        await AsyncStorage.setItem('@search_history', jsonValue);
        readHistory();
      } catch (e) {
        // saving error
        console.error('Error saving history', e);
      }
    }
  };

  const readHistory = async () => {
    try {
      const jsonValue = await AsyncStorage.getItem('@search_history');
      let storedHistory = [];

      if (jsonValue !== null) {
        const parsedData = JSON.parse(jsonValue);
        storedHistory = Array.isArray(parsedData) ? parsedData : [];
      }

      const sortedHistory = storedHistory.sort((a, b) => +b.id - +a.id);
      setHistory(sortedHistory);
    } catch (e) {
      console.error('Error reading history', e);
      setHistory([]);
    }
  };

  useEffect(() => {
    readHistory();
  }, []);

  useEffect(() => {
    state.isSearchFocus && storeRecentSearchTerm();
  }, [state.isSearchFocus]);

  return (
    <Provider
      value={{
        ...state,
        setSearch,
        setViewAllEvent,
        setIsSearchFocus,
        setLocation,
        setIsSearching,
        storeRecentSearchTerm,
        deleteHistoryItem,
        setSearchWidth,
      }}>
      {children}
    </Provider>
  );
};

const useGlobalSearch = (): SearchContext => {
  const context = useContext(searchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a Provider');
  }
  return context;
};

export default useGlobalSearch;
